import { format } from "date-fns";
import type { FC } from "react";
import { useCallback, useContext, useMemo } from "react";
import { Doc } from "sharedb/lib/client";

import { isGroupAccessId, isStudentAccessId } from "../../../../shared/access-id";
import { WorksheetType } from "../../../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../../../shared/worksheets/constants";
import { atLeastOneStudentNotInGroup, findPageGroupIndexOfStudentUser } from "../groups/helpers";
import {
  getLateSubmissionDueDate,
  intervalIsDefined,
  isInLateSubmissionWindow,
  isPastSubmissionDueDate,
  isSubmissionLate,
} from "../helpers";

import { GroupNotAssignedBanner } from "./GroupNotAssignedBanner";
import { OlderSubmissionAssessedBanner } from "./OlderSubmissionAssessedBanner";
import { StudentEditingUnsubmittableBanner } from "./StudentEditingUnsubmittableBanner";
import { StudentsNotInGroupBanner } from "./StudentsNotInGroupBanner";
import { SubmissionStatusBanner } from "./SubmissionStatusBanner";
import { SubmissionsDifferFromResponsesBanner } from "./SubmissionsDifferFromResponsesBanner";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { EmptyPageBanner } from "components/materials/page/banners/EmptyPageBanner";
import { GroupSubmittedByBanner } from "components/materials/page/banners/GroupSubmittedByBanner";
import { InstructorEditingStudentDraftBanner } from "components/materials/page/banners/InstructorEditingStudentDraftBanner";
import { InstructorLateSubmissionBanner } from "components/materials/page/banners/InstructorLateSubmissionBanner";
import { LateSubmissionAvailableBanner } from "components/materials/page/banners/LateSubmissionsAvailableBanner";
import { PageReleasedBanner } from "components/materials/page/banners/PageReleasedBanner";
import { StudentLateSubmissionBanner } from "components/materials/page/banners/StudentLateSubmissionBanner";
import { TemplatePageDisabledBanner } from "components/materials/page/banners/TemplatePageDisabledBanner";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { InteractionMode, type PageType } from "components/server-types";
import { DAY_AND_TIME_FORMAT } from "i18n/helpers";
import { ORDER_BY_CREATED, useCustomOrdered } from "mds/hooks/use-ordered";
import { BypassReleaseWarningContext } from "providers/BypassReleaseWarningProvider";
import { DocViewContext, type PageTabVariant } from "providers/DocViewProvider";
import { useCapabilities, useUserIdsWhoCanParticipateInSection } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  sectionUserSelectors,
  selectActivityStatus,
  selectCurrentSectionUserId,
  selectFullSubmissionById,
  selectHasSubmissionsForPageId,
  selectIsEditModeEnabled,
  selectPageAccessDataForSectionUser,
  selectPageGroupsWithUsersByPageId,
  selectUsersWithSectionActivityInCurrentSection,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { isGroupInteractionMode, pageIsDisabled } from "utils/page-utils";
import { usePresentation, useShowNotFollowingPresentationBanner } from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";

type PageBannersProps = {
  page: PageType;
  doc?: Doc<WorksheetType>;
  selectedTabVariant?: PageTabVariant;
};

export const PageBanners: FC<PageBannersProps> = ({ page, doc, selectedTabVariant }) => {
  const { getQueryParam } = useQueryParamsManager();
  const { accessId, canAuthorPage, isReleased, pageId, readOnly } = useContext(DocViewContext);
  const { showWarning, bypassWarning } = useContext(BypassReleaseWarningContext);
  const { isPresenting, hasProjectorView } = usePresentation();
  const showNotFollowingPresentationBanner = useShowNotFollowingPresentationBanner();

  const { canAuthorSection, canEditStudentDraft, canParticipateInSection } = useCapabilities();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isAssessing = useIsAssessing();
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));
  const submittedBySectionUser = useAppSelector((s) =>
    sectionUserSelectors.selectById(s, submission?.submitted_by_id),
  );
  const hasSubmissions = useAppSelector((s) => selectHasSubmissionsForPageId(s, pageId));

  const blocks = doc?.data?.[BLOCKS_PATH] || [];

  const isSeededContentTab = selectedTabVariant === "seeded_content";
  const isSubmissionsTab =
    selectedTabVariant === "my_submission" ||
    selectedTabVariant === "student_submission" ||
    !!submission;
  const isDraftTab =
    selectedTabVariant && ["my_draft", "class_draft", "template"].includes(selectedTabVariant);
  const isStudentResponsesTab = selectedTabVariant === "responses";
  const isViewingSubmission =
    Boolean(submissionId) && submissionId !== "draft" && submissionId !== "null";

  const submissionIsLate = isSubmissionLate(submission, page?.due_at);
  const lateSubmissionDate =
    page.due_at && page.late_submission_interval && intervalIsDefined(page.late_submission_interval)
      ? format(getLateSubmissionDueDate(page) as Date, DAY_AND_TIME_FORMAT)
      : undefined;
  const afterSubmissionDueDate = isPastSubmissionDueDate(page) && !isInLateSubmissionWindow(page);

  const userActivity = useAppSelector(selectActivityStatus);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const pageGroups = useCustomOrdered(
    useAppSelector((s) => selectPageGroupsWithUsersByPageId(s, page.id)),
    ORDER_BY_CREATED,
  );
  const studentUserIds = useUserIdsWhoCanParticipateInSection();
  const users = useListSelector(selectUsersWithSectionActivityInCurrentSection);
  const studentUsers = useMemo(
    () => users.filter((user) => studentUserIds.includes(user.id)),
    [users, studentUserIds],
  );
  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForSectionUser(s, page.id, sectionUserId),
  );

  const dispatch = useAppDispatch();
  const showGroupConfig = useCallback(
    () => dispatch(viewStateActions.setShowGroupConfig(true)),
    [dispatch],
  );

  const showInstructorEditingStudentDraftBanner =
    isStudentResponsesTab &&
    (isStudentAccessId(accessId) || isGroupAccessId(accessId)) &&
    canEditStudentDraft &&
    isEditModeEnabled &&
    !readOnly &&
    !isViewingSubmission;

  const showPageReleasedBanner =
    isEditModeEnabled &&
    !isSeededContentTab &&
    canAuthorPage &&
    isReleased &&
    !isPresenting &&
    showWarning &&
    !isSubmissionsTab;

  const showSubmissionsDifferFromResponsesBanner =
    canAuthorSection && isStudentResponsesTab && hasSubmissions && !submission;

  const showInstructorLateSubmissionBanner =
    submissionIsLate &&
    canAuthorSection &&
    (selectedTabVariant === "student_submission" || submission);

  const showStudentLateSubmissionBanner =
    submissionIsLate && canParticipateInSection && selectedTabVariant === "my_submission";

  const showLateSubmissionAvailableBanner = isDraftTab && isInLateSubmissionWindow(page);

  const showEditingUnsubmittableDraftBanner =
    selectedTabVariant === "my_draft" &&
    canParticipateInSection &&
    !readOnly &&
    afterSubmissionDueDate;

  const showCurrentStudentNotInGroupBanner =
    canParticipateInSection &&
    page.interaction_mode === InteractionMode.ASSIGN_STUDENTS &&
    findPageGroupIndexOfStudentUser(pageGroups, sectionUserId) === -1;
  const showStudentsNotInGroupBanner =
    !hasProjectorView &&
    canAuthorSection &&
    isGroupInteractionMode(page.interaction_mode) &&
    atLeastOneStudentNotInGroup(pageGroups, studentUsers, userActivity);

  const showTemplatePageDisabledBanner =
    pageIsDisabled(page) && canAuthorSection && !!isDraftTab && isEditModeEnabled;
  const showEmptyPageBanner = canAuthorPage && blocks.length === 0 && !isEditModeEnabled;
  const maybeShowSubmissionStatusBanner = isAssessing && canAuthorSection;
  const maybeShowOlderSubmissionAssessedBanner = maybeShowSubmissionStatusBanner;

  // For group pages, show who submitted the page for the group
  // If no submissions have been collected yet, we show who marked the page as complete
  const isPageSubmittedByStudent = submittedBySectionUser?.role === "student";
  const isPageCompleted = Boolean(
    pageAccessData?.completed_at &&
      pageAccessData?.completed_by_id &&
      !page.submissions_last_collected_at,
  );
  const showGroupSubmittedByBanner =
    canParticipateInSection &&
    isGroupInteractionMode(page.interaction_mode) &&
    (isPageSubmittedByStudent || isPageCompleted);

  if (
    !showTemplatePageDisabledBanner &&
    !showInstructorEditingStudentDraftBanner &&
    !showPageReleasedBanner &&
    !showInstructorLateSubmissionBanner &&
    !showStudentLateSubmissionBanner &&
    !showLateSubmissionAvailableBanner &&
    !showCurrentStudentNotInGroupBanner &&
    !showStudentsNotInGroupBanner &&
    !showEmptyPageBanner &&
    !showSubmissionsDifferFromResponsesBanner &&
    !showNotFollowingPresentationBanner &&
    !showEditingUnsubmittableDraftBanner &&
    !showGroupSubmittedByBanner &&
    !maybeShowSubmissionStatusBanner &&
    !maybeShowOlderSubmissionAssessedBanner
  ) {
    return null;
  }

  return (
    <div className="mb-2 flex w-full max-w-[--page-default-max-width] flex-col gap-4">
      {showTemplatePageDisabledBanner && <TemplatePageDisabledBanner />}
      {showInstructorEditingStudentDraftBanner && (
        <InstructorEditingStudentDraftBanner accessId={accessId} pageId={page.id} />
      )}
      {showSubmissionsDifferFromResponsesBanner && <SubmissionsDifferFromResponsesBanner />}
      {showPageReleasedBanner && <PageReleasedBanner onDismiss={bypassWarning} />}
      {showInstructorLateSubmissionBanner && <InstructorLateSubmissionBanner />}
      {showStudentLateSubmissionBanner && <StudentLateSubmissionBanner />}
      {showGroupSubmittedByBanner && (
        <GroupSubmittedByBanner
          sectionUserId={submission?.submitted_by_id || pageAccessData?.completed_by_id}
        />
      )}
      {showEditingUnsubmittableDraftBanner && <StudentEditingUnsubmittableBanner />}
      {showLateSubmissionAvailableBanner && (
        <LateSubmissionAvailableBanner lateSubmissionDate={lateSubmissionDate} />
      )}
      {showCurrentStudentNotInGroupBanner && <GroupNotAssignedBanner />}
      {showStudentsNotInGroupBanner && (
        <StudentsNotInGroupBanner onReviewGroups={showGroupConfig} />
      )}
      {showEmptyPageBanner && <EmptyPageBanner />}
      {maybeShowSubmissionStatusBanner && <SubmissionStatusBanner doc={doc} page={page} />}
      {maybeShowOlderSubmissionAssessedBanner && <OlderSubmissionAssessedBanner page={page} />}
    </div>
  );
};
